import React, { useEffect, useState } from 'react';
import { FlatList, HStack, Pressable, VStack, Skeleton } from 'native-base';
import { ProductCard } from './ProductCard';
import {
    Product,
    Category,
    Subcategory,
    Effects,
    StrainType,
    MenuSort,
    InputMaybe,
    MenuSectionFilter,
} from '@springbig/shared/src/services/dutchie/schema-types';
import {
    ProductListPageProps,
    useDutchieStore,
    useI18n,
    useMenuQuery
} from '@springbig/shared';
import { useShopStyles } from '~/shared/styles';
import { Caption } from '~/shared/Typography';
import { useAppNavigate } from '~/pages';
import { PotencyUnit } from '@springbig/shared';

export type ProductListProps = {
    title: string;
    search? : string;
    category?: Category;
    subcategory?: Subcategory;
    effect?: InputMaybe<Array<Effects>>;
    strain?: InputMaybe<StrainType>;
    menuSection?: MenuSectionFilter;
    limit?: number;
    menuSort?: MenuSort;
};

export const ProductList = ({
    title,
    category,
    subcategory,
    effect,
    strain,
    menuSection,
    limit = 10,
    menuSort,
}: ProductListProps) => {

    const {
        retailer,
        menuType,
        isBusy,
        filters,
        setProductListProps
    } = useDutchieStore();
    const { mutate, isLoading } = useMenuQuery();
    const { pressable_text, primary_color, section_background } = useShopStyles();
    const { navigate, params } = useAppNavigate();

    const [offset, setOffset] = useState(0);
    const [products, setProducts] = useState<Array<Product> | undefined>();
    const { t } = useI18n();

    useEffect(() => {

        setProducts([]);
    }, [retailer, effect, strain, menuSort, filters?.minPrice, filters?.maxPrice]);

    const createVariables = () => ({
        retailerId: retailer?.id,
        menuType,
        filter: {
            category,
            subcategory,
            effects: effect,
            strainType: strain,
            menuSection: menuSection ? { type: menuSection.type, specialId: menuSection.specialId } : undefined,
            potencyCbd: { max: filters?.potencyCbd?.max, min: filters?.potencyCbd?.min, unit: PotencyUnit.Percentage },
            potencyThc: { max: filters?.potencyThc?.max, min: filters?.potencyThc?.min, unit: PotencyUnit.Percentage },
            brandIds: filters?.brandIds,
            categories: filters?.categories
        },
        sort: { direction: menuSort?.direction, key: menuSort?.key },
        pagination: { offset, limit }
    });

    const filterProductsByPrice = (oldProducts : Product[], newProducts : Product[]) => {

        if (filters?.minPrice !== undefined && filters?.maxPrice !== undefined) {
            const minValue = filters.minPrice;
            const maxValue = filters.maxPrice;
            return [...oldProducts, ...newProducts].filter(e => {

                const price = menuType === 'MEDICAL' ? e.variants[0].specialPriceMed ?? e.variants[0].priceMed ?? 0 : e.variants[0].specialPriceRec ?? e.variants[0].priceRec ?? 0;
                return price <= maxValue && price >= minValue;
            });
        }
    };

    useEffect(() => {

        if (retailer && menuType && (category || menuSection) && menuSort && !isLoading) {

            const variables = createVariables();

            if (!isLoading) {
                mutate(variables, {
                    onSuccess: (data) => {

                        if (data && data.menu.products.length) {
                            if (filters?.minPrice || filters?.maxPrice) {
                                setProducts(prevState => filterProductsByPrice(prevState ?? [], data.menu.products));
                            }
                            else {
                                setProducts(prevState => (offset === 0 ? data.menu.products : [...(prevState ?? []), ...data.menu.products]));
                            }
                        }
                    },
                });
            }
        }
    }, [
        retailer,
        menuType,
        category,
        subcategory,
        effect,
        strain,
        menuSection,
        limit,
        menuSort,
        offset,
        filters?.minPrice,
        filters?.maxPrice
    ]);

    return (
        (products && products?.length > 0) ?
            <VStack ml={2} mb={4}>
                <HStack space={2}>
                    <Caption>{ title }</Caption>
                    {products?.length < 10 ? <></> : <Pressable
                        opacity={isBusy ? .7 : 1}
                        isDisabled={isBusy}
                        onPress={() => {

                            const productListProps: ProductListPageProps = {
                                title,
                                category,
                                subCategory: subcategory,
                                menuSection
                            };
                            setProductListProps(productListProps);
                            navigate(`w/${params.wallet_id}/shop/product_list`);
                        }}
                    >
                        <Caption color={pressable_text}>
                            { t('shop.see_all') }
                        </Caption>
                    </Pressable>}
                </HStack>
                <FlatList
                    py={2}
                    horizontal={true}
                    data={products?.filter(p => p.variants.length >= 1)}
                    keyExtractor={(item : Product, index : number) => (`${item.id}-${index}-${item.name}`)}
                    renderItem={({ item, index }: { item: Product, index: number }) =>

                        <ProductCard item={item} key={`${item.id}-${index}-${item.name}`} />
                    }
                    onEndReached={() => {

                        if (products.length >= limit) {
                            setOffset(offset + limit);
                        }
                    }}
                    onEndReachedThreshold={0.7}
                />
            </VStack>
            :  isLoading ?
                <VStack space={2} px={2}>
                    <Skeleton
                        w={120}
                        h={6}
                        rounded={'xl'}
                        startColor={primary_color}
                        endColor={section_background}
                    />
                    <HStack space={2}>
                        { [...Array(4)].map((_, index) =>

                            <Skeleton
                                w={120}
                                h={240}
                                rounded={'xl'}
                                startColor={primary_color}
                                endColor={section_background}
                                key={index}
                            />
                        )}
                    </HStack>

                </VStack> :
                <></>
    );
};
